/**
 * EPIC-DM01-C: Demo Processing Page
 * Shows analysis progress with circular indicator, step list, and info cards.
 *
 * Design Reference: Figma Processing Screen (iPad + phone)
 *
 * Backend stages (5): uploading → processing → pose → analysis → ready | failed
 * UI stages (4):      Uploading Video → Extracting Frames → AI Analysis → Generating Report
 * The `pose` and `analysis` backend stages both map to the "AI Analysis" UI step.
 */

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDemoMode, type DemoSession } from '@/hooks/useDemoMode';
import { useDemoProcessingStatus } from '@/hooks/useDemoResults';
import { usePipelineDiagnostics } from '@/hooks/usePipelineDiagnostics';
import { DemoButton, DemoCard, DemoChip, DemoProgress } from '@/components/demo/ui';
import { AlertCircle, ArrowLeft, Check, Loader2, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ================================================================== */
/*  Types & Constants                                                  */
/* ================================================================== */

type BackendStage = 'uploading' | 'processing' | 'pose' | 'analysis' | 'ready' | 'failed';

interface UIStageConfig {
  id: string;
  label: string;
  description: string;
}

/** The 4 user-visible processing steps shown in the step list. */
const UI_STAGES: UIStageConfig[] = [
  { id: 'uploading', label: 'Uploading Video', description: 'Sending your swing to the cloud...' },
  { id: 'processing', label: 'Extracting Frames', description: 'Breaking down your swing frame by frame...' },
  { id: 'analysis', label: 'AI Analysis', description: 'Our coaching engine is evaluating your swing...' },
  { id: 'ready', label: 'Generating Report', description: 'Preparing your personalized results...' },
];

/**
 * Map a backend stage to the active UI step index.
 * Returns an index past the end (4) when all steps are complete,
 * or -1 when the run has failed.
 */
function getActiveUIIndex(backendStage: BackendStage): number {
  switch (backendStage) {
    case 'uploading':
      return 0;
    case 'processing':
      return 1;
    case 'pose':
    case 'analysis':
      return 2;
    case 'ready':
      return 4; // past last index → all complete
    case 'failed':
      return -1;
    default:
      return 0;
  }
}

/** Fun golf facts — one is chosen at random on mount. */
const FUN_FACTS = [
  'The average recreational golfer loses 12+ yards from swing inefficiencies that are easy to fix with targeted practice.',
  'Most swing faults can be improved by 40% within just 2-4 weeks of focused drill work.',
  'AI swing analysis can detect movement patterns invisible to the naked eye.',
  'Tour pros practice specific drills for only 15-20 minutes per session — quality over quantity.',
];

/* ================================================================== */
/*  Step Row                                                           */
/* ================================================================== */

interface StepRowProps {
  index: number;
  config: UIStageConfig;
  activeIndex: number;
  isFailed: boolean;
}

/** Single row in the step list: number circle, label, and status chip. */
function StepRow({ index, config, activeIndex, isFailed }: StepRowProps) {
  const isComplete = index < activeIndex;
  const isActive = index === activeIndex && !isFailed;

  const chipStatus = isComplete ? 'complete' as const : isActive ? 'processing' as const : 'idle' as const;
  const chipLabel = isComplete ? 'Complete' : isActive ? 'In Progress' : 'Pending';

  return (
    <div className="flex items-center gap-4 min-h-[var(--demo-touch-min)] py-2">
      {/* Number / check circle */}
      <div
        className={cn(
          'flex items-center justify-center w-10 h-10 rounded-full shrink-0 text-demo-base font-bold',
          isComplete && 'bg-demo-green text-white',
          isActive && 'bg-demo-gold text-white',
          !isComplete && !isActive && 'bg-gray-200 text-demo-text-muted',
        )}
        aria-hidden="true"
      >
        {isComplete ? <Check className="h-5 w-5" /> : index + 1}
      </div>

      {/* Step label */}
      <span
        className={cn(
          'flex-1 text-demo-base font-semibold',
          isComplete && 'text-demo-green',
          isActive && 'text-demo-text',
          !isComplete && !isActive && 'text-demo-text-muted',
        )}
      >
        {config.label}
      </span>

      {/* Status chip */}
      <DemoChip
        status={chipStatus}
        icon={
          isComplete ? <Check className="h-4 w-4" /> :
          isActive ? <Loader2 className="h-4 w-4 animate-spin" /> :
          undefined
        }
      >
        {chipLabel}
      </DemoChip>
    </div>
  );
}

/* ================================================================== */
/*  Main Component                                                     */
/* ================================================================== */

export default function DemoProcessing() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const {
    canAccessDemoMode,
    isDemoModeEnabled,
    isLoading: isLoadingConfig,
    getSession,
  } = useDemoMode();

  const [session, setSession] = useState<DemoSession | null>(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);

  /** Random fun fact, chosen once on mount. */
  const [funFact] = useState(
    () => FUN_FACTS[Math.floor(Math.random() * FUN_FACTS.length)],
  );

  const { recordStage } = usePipelineDiagnostics();
  const resultAvailableRecorded = useRef(false);

  // ── Processing status polling ────────────────────────────────────
  const {
    stage,
    progress,
    statusText,
    status,
    isComplete,
    isFailed,
    errorMessage,
  } = useDemoProcessingStatus(sessionId || '', {
    enabled: !!sessionId && !isLoadingSession && !sessionError,
    pollingInterval: 3000,
    onComplete: () => {
      // Navigate to results after a brief delay
      setTimeout(() => {
        navigate(`/demo/results/${sessionId}`);
      }, 1500);
    },
  });

  // OPS-19: Record result_available when demo analysis is ready (once per run)
  useEffect(() => {
    if (stage !== 'ready' || !status?.run_id || resultAvailableRecorded.current) return;
    resultAvailableRecorded.current = true;
    try {
      recordStage({
        runId: status.run_id,
        pipelineType: 'demo',
        stage: 'result_available',
        clientRecordedAt: new Date().toISOString(),
      });
    } catch (_) {
      /* non-blocking */
    }
  }, [stage, status?.run_id, recordStage]);

  // ── Derived UI state ─────────────────────────────────────────────
  const activeUIIndex = getActiveUIIndex(stage as BackendStage);

  /** Estimated seconds remaining shown below the circular progress. */
  const timeEstimate = useMemo(() => {
    if (progress >= 100 || isFailed) return undefined;
    return `Est. ${Math.max(5, Math.round((100 - progress) * 0.9))}s remaining`;
  }, [progress, isFailed]);

  /** Description text for the "What's Happening?" card. */
  const activeDescription = useMemo(() => {
    if (isFailed) return 'Something went wrong during analysis. You can try again.';
    if (isComplete) return 'Your personalized swing report is ready!';
    const idx = Math.min(Math.max(0, activeUIIndex), UI_STAGES.length - 1);
    return UI_STAGES[idx].description;
  }, [activeUIIndex, isFailed, isComplete]);

  // ── Load session on mount ────────────────────────────────────────
  useEffect(() => {
    async function loadSession() {
      if (!sessionId) {
        setSessionError('No session ID provided');
        setIsLoadingSession(false);
        return;
      }

      try {
        const loadedSession = await getSession(sessionId);
        if (!loadedSession) {
          setSessionError('Session not found');
        } else {
          setSession(loadedSession);

          // If session is already complete, go straight to results
          if (loadedSession.status === 'complete') {
            navigate(`/demo/results/${sessionId}`);
            return;
          }
        }
      } catch (err) {
        console.error('Failed to load session:', err);
        setSessionError('Failed to load session');
      } finally {
        setIsLoadingSession(false);
      }
    }

    loadSession();
  }, [sessionId, getSession, navigate]);

  // ── Retry handler ────────────────────────────────────────────────
  const handleRetry = useCallback(() => {
    navigate(`/demo/capture/${sessionId}`);
  }, [navigate, sessionId]);

  // ────────────────────────────────────────────────────────────────
  //  Guard screens (loading / denied / session error)
  // ────────────────────────────────────────────────────────────────

  if (isLoadingConfig || isLoadingSession) {
    return (
      <div className="demo-mode min-h-screen bg-[var(--demo-surface-alt)] flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-demo-green" />
          <p className="text-demo-base text-demo-text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  if (!canAccessDemoMode || !isDemoModeEnabled) {
    return (
      <div className="demo-mode min-h-screen bg-[var(--demo-surface-alt)] flex items-center justify-center p-4">
        <DemoCard className="max-w-md w-full text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-demo-error" />
          <h2 className="text-demo-xl font-bold mb-2 text-demo-text">Access Denied</h2>
          <p className="text-demo-base text-demo-text-muted mb-6">
            You do not have permission to access Demo Mode.
          </p>
          <DemoButton variant="outline" onClick={() => navigate('/home')}>
            Return to Home
          </DemoButton>
        </DemoCard>
      </div>
    );
  }

  if (sessionError || !session) {
    return (
      <div className="demo-mode min-h-screen bg-[var(--demo-surface-alt)] flex items-center justify-center p-4">
        <DemoCard className="max-w-md w-full text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-demo-error" />
          <h2 className="text-demo-xl font-bold mb-2 text-demo-text">Session Error</h2>
          <p className="text-demo-base text-demo-text-muted mb-6">
            {sessionError || 'Unable to load session'}
          </p>
          <DemoButton variant="outline" onClick={() => navigate('/demo')}>
            Return to Demo Landing
          </DemoButton>
        </DemoCard>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────
  //  Main processing view
  // ────────────────────────────────────────────────────────────────

  return (
    <div className="demo-mode min-h-screen bg-[var(--demo-surface-alt)]">
      {/* ── Header ───────────────────────────────────────────────── */}
      <header className="bg-demo-surface border-b border-gray-200 sticky top-0 z-10">
        <div className="mx-auto max-w-xl px-4 py-5 flex items-center gap-4">
          <DemoButton
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/demo/select/${sessionId}`)}
            disabled={!isFailed}
            className={cn(!isFailed && 'invisible')}
            aria-label="Back to Selection"
          >
            <ArrowLeft className="h-5 w-5" />
          </DemoButton>

          <div className="flex-1 text-center">
            <h1 className="text-demo-xl font-bold text-demo-text">
              {isFailed
                ? 'Analysis Failed'
                : isComplete
                  ? 'Analysis Complete!'
                  : 'Analyzing Your Swing'}
            </h1>
            <p className="text-demo-base text-demo-text-muted">Step 4 of 4</p>
          </div>

          {/* Spacer to keep title centred */}
          <div className="w-10 shrink-0" aria-hidden="true" />
        </div>
      </header>

      {/* ── Content ──────────────────────────────────────────────── */}
      <main className="mx-auto max-w-xl px-4 py-8 flex flex-col gap-6">
        {/* Circular progress */}
        {!isFailed && (
          <div className="flex justify-center py-2">
            <DemoProgress
              mode="circle"
              value={progress}
              label={statusText}
              timeEstimate={timeEstimate}
            />
          </div>
        )}

        {/* Step list */}
        <DemoCard>
          <div className="flex flex-col divide-y divide-gray-100">
            {UI_STAGES.map((stageConfig, index) => (
              <StepRow
                key={stageConfig.id}
                index={index}
                config={stageConfig}
                activeIndex={activeUIIndex}
                isFailed={isFailed}
              />
            ))}
          </div>
        </DemoCard>

        {/* Error state */}
        {isFailed && (
          <DemoCard variant="warning" className="text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-demo-gold-dark" />
            <h3 className="text-demo-xl font-bold text-demo-text mb-2">
              Analysis Failed
            </h3>
            <p className="text-demo-base text-demo-text-muted mb-2">
              {errorMessage || 'Something went wrong during analysis.'}
            </p>
            <p className="text-demo-sm text-demo-text-muted mb-6">
              Please try again with a clear video of your golf swing.
            </p>
            <DemoButton
              onClick={handleRetry}
              iconLeft={<RefreshCw className="h-5 w-5" />}
            >
              Try Again
            </DemoButton>
          </DemoCard>
        )}

        {/* Success state */}
        {isComplete && (
          <DemoCard variant="highlight" className="text-center">
            <Check className="h-12 w-12 mx-auto mb-4 text-demo-green" />
            <h3 className="text-demo-xl font-bold text-demo-green mb-2">
              Analysis Complete!
            </h3>
            <p className="text-demo-base text-demo-text-muted mb-4">
              Redirecting to your results...
            </p>
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-demo-green" />
          </DemoCard>
        )}

        {/* "What's Happening?" explainer */}
        {!isFailed && !isComplete && (
          <DemoCard>
            <h3 className="text-demo-xl font-bold text-demo-text mb-2">
              What&apos;s Happening?
            </h3>
            <p className="text-demo-base text-demo-text-muted">
              {activeDescription}
            </p>
          </DemoCard>
        )}

        {/* "Did you know?" fun fact */}
        {!isFailed && !isComplete && (
          <DemoCard variant="highlight">
            <h3 className="text-demo-xl font-bold text-demo-text mb-2">
              Did you know?
            </h3>
            <p className="text-demo-base text-demo-text-muted">
              {funFact}
            </p>
          </DemoCard>
        )}
      </main>
    </div>
  );
}
